import { NotificationCenterCategory } from '../NotificationCenter/NotificationCenter';

export enum EventLogActionType {
    created = 'created',
    updated = 'updated',
    deleted = 'deleted',
    notification = 'notification',
}

type EventLogDataItemBodyType = 'Shift'

interface EventLogDataItemBodyEntityResource {
    id?: string;
    uuid?: string;
    type?: EventLogDataItemBodyType;
    fullName?: string;
}

export interface EventLogDataItemBodyResource {
    _summary: string;
    _entity: EventLogDataItemBodyEntityResource
    [key: string]: Record<string, any> | string;
}

export interface EventLogDataItemResource {
    entityUuid: string
    entityType: EventLogEntityTypeResource;
    action: EventLogActionType;
    category: NotificationCenterCategory;
    title: string;
    subTitle: string;
    body: EventLogDataItemBodyResource;
}

export interface EventLogResource {
    uuid: string;
    actionUuid: string;
    event: string;
    data: EventLogDataItemResource;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
}

export interface EventLogMailNotification {
    body: string | null;
    subject: string | null;
    recipients: string[];
}

export interface EventLogWeekHours {
    week: number;
    hours?: string;
    waitingDayHours?: string;
    leaveType?: string;
}

export type EventLogWeeksHours = Record<string, EventLogWeekHours> | { hours?: string; waitingDayHours?: string; leaveType?: string; };

export enum EntityCategory {
    Absence = 'absence',
    Address = 'address',
    MailEventLog = 'mailEventLog',
    DivergentPreEmploymentHour = 'divergentPreEmploymentHour',
    Employment = 'employment',
    LeaveOfAbsence = 'leaveOfAbsence',
    LeaveOfAbsenceHour = 'leaveOfAbsenceHour',
    Person = 'person',
    Shift = 'shift',
    ShiftPlanning = 'shiftPlanning',
    ShiftPlanningRemoval = 'shiftPlanningRemoval',
    User = 'user',
    Comment = 'comment',
    MailNotification = 'mailNotification',
}

export enum EntityCategoryItem {
    // Address
    address = 'address',
    houseNumber = 'houseNumber',
    houseNumberAddition = 'houseNumberAddition',
    country = 'country',
    city = 'city',
    postalCode = 'postalCode',
    street = 'street',

    // Contract
    contractHours = 'contractHours',
    contractHoursPeriod = 'contractHoursPeriod',

    // Employment
    start = 'start',
    employment = 'employment',
    employmentType = 'employmentType',

    // Person
    person = 'person',
    firstname = 'firstname',
    lastname = 'lastname',
    fullName = 'fullName',
    gender = 'gender',
    experience = 'experience',
    affix = 'affix',
    workDays = 'workDays',
    email = 'email',
    isCompanyMedic = 'isCompanyMedic',
    nationality = 'nationality',
    birthday = 'birthday',
    userName = 'userName',

    // Shift
    peopleLimit = 'peopleLimit',
    plannedUsers = 'plannedUsers',
    planned = 'planned',

    // Track
    track = 'track',
    checkIn = 'checkIn',
    checkOut = 'checkOut',
    billableStart = 'billableStart',
    billableEnd = 'billableEnd',
    meal = 'meal',
    pause = 'pause',
    unplannedRemark = 'unplannedRemark',
    deviationRemark = 'deviationRemark',
    isOffline = 'isOffline',
    accepted = 'accepted',
    department = 'department',
    feedback = 'feedback',
    approval = 'approval',
    pauseMealFeedback = 'pauseMealFeedback',

    // User
    phone = 'phone',
    isEligibleForTimeForTime = 'isEligibleForTimeForTime',
    employeeNumber = 'employeeNumber',
    relationType = 'relationType',
    shouldTrackTime = 'shouldTrackTime',
    isPaidBasedOnTrackedTime = 'isPaidBasedOnTrackedTime',
    hasSundayAllowance = 'hasSundayAllowance',
    tenant = 'tenant',
    departments = 'departments',
    mainDepartment = 'mainDepartment',
    currentUserName = 'currentUserName',
    activityStatus = 'activityStatus',

    // Mailer
    sender = 'sender',
    recipient = 'recipient',
    subject = 'subject',
    contents = 'contents',

    // Absence
    leaveType = 'leaveType',
    leaveOfAbsenceHour = 'leaveOfAbsenceHour',
    reason = 'reason',
    declineReason = 'declineReason',
    hours = 'hours',
    waitingDayHours = 'waitingDayHours',
    reviewedByUser = 'reviewedByUser',

    // Other
    id = 'id',
    uuid = 'uuid',
    status = 'status',
    action = 'action',
    end = 'end',
    category = 'category',
    meta = 'meta',
    tags = 'tags',
    identificationExpirationDate = 'identificationExpirationDate',
    identificationType = 'identificationType',
    shift = 'shift',
    shiftPlanning = 'shiftPlanning',
    comment = 'comment',
    color = 'color',
    user = 'user',
    employee = 'employee',
    locked = 'locked',
    loket = 'loket',
    loketPayrollId = 'loketEmployeeId',
}

export enum EventLogEntityTypeResource {
    absence = 'App\\Entity\\Absence',
    absenceHour = 'App\\\\Entity\\\\AbsenceHour',
    comment = 'App\\\\Entity\\\\Comment',
    department = 'App\\Entity\\Department',
    employment = 'App\\\\Entity\\\\Employment',
    leaveOfAbsence = 'App\\Entity\\LeaveOfAbsence',
    mail = 'App\\\\V3\\\\Helper\\\\EventLogger\\\\DTO\\\\MailEventLog',
    mailV2 = 'App\\Builder\\Mail\\Mail',
    other = 'other',
    period = 'App\\Entity\\PayrollPeriod',
    periodWeek = 'App\\Entity\\PayrollPeriodWeek',
    setting = 'App\\Entity\\Setting',
    shift = 'App\\Entity\\Shift',
    track = 'App\\Entity\\Track',
    user = 'App\\\\Entity\\\\User',
    person = 'App\\\\Entity\\\\Person',
    divergentPreEmploymentHour = 'App\\\\Entity\\\\DivergentPreEmploymentHour',
}

export enum EventLogDataType {
    user = 'User',
    shift = 'Shift',
    person = 'Person',
    address = 'Address',
    emergencyContact = 'EmergencyContact',
    employment = 'Employment',
    eventTracker = 'EventTracker',
    mailEventLog = 'MailEventLog',
    addressPerson = 'AddressPerson',
    divergentPreEmploymentHour = 'DivergentPreEmploymentHour',
    mailNotification = 'MailNotification',
    track = 'Track',
    approval = 'Approval',
    feedback = 'Feedback',
    setting = 'Setting',
    body = 'body',
}

export const userEntityTypes: EventLogEntityTypeResource[] = [
    EventLogEntityTypeResource.user,
    EventLogEntityTypeResource.person,
    EventLogEntityTypeResource.employment,
];

export interface EventLogDataItem {
    id: string;
    type: string;
    // @TODO: Check if EventLogDataType can be removed, because it is not showed in the UI anymore
    data: Partial<{[K in EventLogDataType]: Record<string, string> | string | string[] | EventLogMailNotification}>;
}

export interface EventLog {
    id: string;
    entityId: string;
    action: EventLogActionType;
    name: string;
    type: EventLogEntityTypeResource;
    categorySlug: string;
    items: EventLogDataItem[];
    message: string;
    createdAt: Date;
    createdBy: string;
    updatedAt: Date;
}
